import { useEffect, useState } from 'preact/hooks'
import { Download, Share, X } from 'lucide-preact'
import {
  canPromptInstall,
  dismissInstall,
  installDismissed,
  isIos,
  isStandalone,
  promptInstall,
  subscribeInstall,
} from '../lib/install'

/**
 * "Installer l'application" — a floating offer, because the browser's own is
 * unreliable.
 *
 * `src/lib/install.ts` explains why: Chrome offers once and then goes quiet for
 * months, and Safari never offers at all. This is the app asking instead, on the
 * screen it opens on, and it disappears for good once it is either taken up or
 * waved away.
 *
 * Two shapes, and they are genuinely different things rather than one with a
 * fallback. Where Chrome has handed us a deferred prompt, the button opens the
 * real install dialog. On iOS there is no dialog to open and no API that can
 * open one, so the button expands into the two taps the user has to perform
 * themselves — saying so plainly beats a button that appears to do nothing.
 */
export function InstallPrompt() {
  // Read once, before the first paint, so an installed app never flashes a
  // banner offering to install it.
  const [hidden, setHidden] = useState(() => isStandalone() || installDismissed())
  const [available, setAvailable] = useState(canPromptInstall)
  const [showSteps, setShowSteps] = useState(false)

  // The event may already have fired and been captured by `watchInstall()`
  // before this component mounted, which is exactly why the event is held there
  // rather than here — so this only has to learn when that changes.
  useEffect(() => subscribeInstall(() => setAvailable(canPromptInstall())), [])

  const ios = isIos(navigator.userAgent, navigator.maxTouchPoints)

  // Nothing to offer: not iOS, and Chrome has not decided the visit is worth an
  // install. Rendering a button that cannot do anything would be worse than
  // rendering none.
  const showing = !hidden && (available || ios)

  // The banner is fixed, so without this it floats over "Ajouter une tâche".
  // Done as a class on `body` rather than as state passed up through the page,
  // because the shell's padding is the only thing that has to know, and the
  // class is removed on unmount — including the unmount that dismissing causes.
  useEffect(() => {
    if (!showing) return
    document.body.classList.add('install-open')
    return () => document.body.classList.remove('install-open')
  }, [showing])

  if (!showing) return null

  function close() {
    setHidden(true)
    dismissInstall()
  }

  async function install() {
    const outcome = await promptInstall()
    // "dismissed" leaves the banner alone on purpose: Chrome will not offer
    // again by itself, so this is the only way back. The × is how you say no.
    if (outcome === 'accepted') close()
  }

  return (
    <aside class="install-banner">
      <button class="install-close" onClick={close} aria-label="Masquer">
        <X size={16} />
      </button>

      {available ? (
        <button class="install-action" onClick={() => void install()}>
          <Download size={18} />
          <span>
            <strong>Installer Maison Zen</strong>
            <small>Sur l’écran d’accueil, comme une vraie app.</small>
          </span>
        </button>
      ) : (
        <button class="install-action" onClick={() => setShowSteps((open) => !open)}>
          <Share size={18} />
          <span>
            <strong>Ajouter à l’écran d’accueil</strong>
            <small>{showSteps ? 'Masquer les étapes' : 'Deux étapes, dans Safari.'}</small>
          </span>
        </button>
      )}

      {showSteps && (
        <ol class="install-steps">
          <li>
            Touche <strong>Partager</strong> en bas de Safari.
          </li>
          <li>
            Choisis <strong>Sur l’écran d’accueil</strong>.
          </li>
        </ol>
      )}
    </aside>
  )
}
